const block_size = {
  MONTH: "MONTH",
  DAY: "DAY",
  WEEK: "WEEK",
  HOUR: "HOUR"
};

// Returns the number of days between two Date-objects as a float
export function get_days_between(a, b) {
  let delta_ms = b.getTime() - a.getTime();
  let delta_days = delta_ms / (1000 * 60 * 60 * 24);
  return delta_days;
}

// Returns the resolution settings appropriate for a given time span
export function get_resolution_for_timespan(from_date, to_date) {
  let days_between = get_days_between(from_date, to_date);

  if (days_between >= 365) {
    return {
      name: "Year",
      block_size: block_size.MONTH,
      resolution_per_block: 32
    };
  }
  if (days_between >= 182) {
    return {
      name: "Half-year",
      block_size: block_size.MONTH,
      resolution_per_block: 96
    };
  }
  if (days_between >= 30) {
    return {
      name: "Month",
      block_size: block_size.MONTH,
      resolution_per_block: 96
    };
  }
  if (days_between >= 14) {
    return {
      name: "Forthnight",
      block_size: block_size.WEEK,
      resolution_per_block: 96
    };
  }
  if (days_between >= 7) {
    return {
      name: "Week",
      block_size: block_size.WEEK,
      resolution_per_block: 96
    };
  }
  if (days_between >= 1) {
    return {
      name: "Day",
      block_size: block_size.DAY,
      resolution_per_block: 96
    };
  }
  return {
    name: "Hours",
    block_size: block_size.HOUR,
    resolution_per_block: 3600
  };
}

const BUCKET_LADDER_S = [
  1, 2, 5, 10, 15, 30,
  60, 120, 300, 600, 900, 1800,
  3600, 7200, 10800, 21600, 43200,
  86400, 172800, 604800
];
const PIXELS_PER_BUCKET = 2;
const BUCKETS_PER_BLOCK = 500;
const MAX_BLOCK_S = 92 * 86400;
const KEEP_MIN_FRACTION = 0.35;
const KEEP_MAX_FRACTION = 2.0;

// Adaptive resolution is on unless localStorage "adaptiveResolution" is "0",
// which falls back to the fixed per-timespan levels for comparison.
export function adaptive_resolution_enabled() {
  try {
    return window.localStorage.getItem("adaptiveResolution") !== "0";
  } catch {
    return true;
  }
}

function format_bucket(seconds) {
  if (seconds % 86400 === 0) return `${seconds / 86400} d`;
  if (seconds % 3600 === 0) return `${seconds / 3600} h`;
  if (seconds % 60 === 0) return `${seconds / 60} min`;
  return `${seconds} s`;
}

export function get_adaptive_resolution(display_from, display_to, plot_width_px, current_bucket_s = null) {
  let span_s = Math.max((display_to.getTime() - display_from.getTime()) / 1000, 1);
  let target_buckets = Math.max(plot_width_px / PIXELS_PER_BUCKET, 50);

  let bucket_s = null;
  if (current_bucket_s) {
    let buckets_in_view = span_s / current_bucket_s;
    if (buckets_in_view >= target_buckets * KEEP_MIN_FRACTION &&
        buckets_in_view <= target_buckets * KEEP_MAX_FRACTION) {
      bucket_s = current_bucket_s;
    }
  }
  if (bucket_s == null) {
    let ideal_s = span_s / target_buckets;
    bucket_s = BUCKET_LADDER_S.find(b => b >= ideal_s) ?? BUCKET_LADDER_S[BUCKET_LADDER_S.length - 1];
  }

  let buckets_per_block = Math.max(1, Math.min(BUCKETS_PER_BLOCK, Math.floor(MAX_BLOCK_S / bucket_s)));
  return {
    name: "Adaptive " + format_bucket(bucket_s),
    bucket_s,
    block_ms: bucket_s * buckets_per_block * 1000,
    resolution_per_block: buckets_per_block
  };
}

export function get_adaptive_blocks_for_timespan(from_date, to_date, resolution) {
  let block_ms = resolution.block_ms;
  let start_ms = Math.floor(from_date.getTime() / block_ms) * block_ms;
  let blocks_to_get = [];
  do {
    let next_block = {
      from: new Date(start_ms),
      to: new Date(start_ms + block_ms),
      max_values: resolution.resolution_per_block
    };
    next_block.hash = hash_from_block(next_block);
    blocks_to_get.push(next_block);
    start_ms += block_ms;
  } while (start_ms < to_date.getTime());
  return blocks_to_get;
}

export function round_down_to_closest_hour(time) {
  return new Date(
    Date.UTC(
      time.getUTCFullYear(),
      time.getUTCMonth(),
      time.getUTCDate(),
      time.getUTCHours(),
      0
    )
  );
}

export function round_down_to_closest_day(time) {
  return new Date(
    Date.UTC(time.getUTCFullYear(), time.getUTCMonth(), time.getUTCDate(), 0, 0)
  );
}

export function round_down_to_closest_month(time) {
  return new Date(Date.UTC(time.getUTCFullYear(), time.getUTCMonth()));
}

export function get_end_of_block(start_of_block, size_of_block) {
  switch (size_of_block) {
    case block_size.MONTH:
      return new Date(
        Date.UTC(
          start_of_block.getUTCFullYear(),
          start_of_block.getUTCMonth() + 1
        )
      );
    case block_size.WEEK:
      return new Date(
        Date.UTC(
          start_of_block.getUTCFullYear(),
          start_of_block.getUTCMonth(),
          start_of_block.getUTCDate() + 7
        )
      );
    case block_size.DAY:
      return new Date(
        Date.UTC(
          start_of_block.getUTCFullYear(),
          start_of_block.getUTCMonth(),
          start_of_block.getUTCDate() + 1
        )
      );
    default:
    case block_size.HOUR:
      return new Date(
        Date.UTC(
          start_of_block.getUTCFullYear(),
          start_of_block.getUTCMonth(),
          start_of_block.getUTCDate(),
          start_of_block.getUTCHours() + 1
        )
      );
  }
}

export function get_start_of_first_block(from, blocksize) {
  switch (blocksize) {
    case block_size.HOUR:
      return round_down_to_closest_hour(from);
    case block_size.DAY:
      return round_down_to_closest_day(from);
    case block_size.MONTH:
    default:
      return round_down_to_closest_month(from);
  }
}

export function get_recording_blocks_for_timespan(from_date,
                                                  to_date,
                                                  display_from,
                                                  display_to) {
  let resolution_level = get_resolution_for_timespan(display_from, display_to);
  let start_of_block = get_start_of_first_block(
    from_date,
    resolution_level.block_size
  );
  let end_of_block = 0;
  let blocks_to_get = [];

  do {
    end_of_block = get_end_of_block(
      start_of_block,
      resolution_level.block_size
    );

    let next_block = {
      from: start_of_block,
      to: end_of_block,
      max_values: resolution_level.resolution_per_block
    };

    next_block.hash = hash_from_block(next_block);
    blocks_to_get.push(next_block);
    start_of_block = end_of_block;
  } while (start_of_block < to_date);

  return blocks_to_get;
}

export function hash_from_block(block) {
  return (
    (block.from.getTime() * 17 +
      block.to.getTime() +
      block.max_values * block.max_values) %
    8503057
  );
}
